import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../../../../Constants';

/** Helpers to parse numeric or letter codes from the labels. */
function parseNumericCode(label = '') {
  // e.g. "06. Car door/gate" => "06"
  const match = label.match(/^(\d{1,3})\.\s/);
  return match ? match[1] : '';
}
function parseLetterCode(label = '') {
  // e.g. "H. Worn" => "H"
  const match = label.match(/^([A-Za-z]+)\.\s/);
  return match ? match[1] : '';
}

/**
 * A read‐only table that:
 *  - Fetches violations by (inspectionNumber, deviceId) from /api/violations/byInspectionAndDevice
 *  - Shows P, V, R codes, plus a smaller set of columns (omitting inspectionId/deviceId).
 */
const ViolationsTable = ({
  inspectionNumber,
  deviceId,
  needsRefresh,  // if changed, triggers a refetch
}) => {
  const [violations, setViolations] = useState([]);

  useEffect(() => {
    if (!inspectionNumber || !deviceId) return;

    async function fetchViolations() {
      try {
        const url = `${API_BASE_URL}/api/violations/byInspectionAndDevice/${inspectionNumber}/${deviceId}`;
        const resp = await fetch(url, { credentials: 'include' });
        if (!resp.ok) {
          throw new Error(`Failed to fetch violations. Status: ${resp.status}`);
        }
        const data = await resp.json();
        if (Array.isArray(data)) {
          setViolations(data);
        } else {
          setViolations([]);
        }
      } catch (err) {
        console.error('Error fetching violations:', err);
        setViolations([]);
      }
    }

    fetchViolations();
  }, [inspectionNumber, deviceId, needsRefresh]);

  return (
    <div className="mt-4">
      <h3 className="text-lg font-bold mb-2">Existing Violations</h3>

      <div className="overflow-auto border border-gray-300 rounded">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-gray-100">
            <tr>
              {/* Show codes for part/condition/remedy */}
              <th className="border p-2 text-center">P</th>
              <th className="border p-2 text-center">V</th>
              <th className="border p-2 text-center">R</th>

              {/* Minimal columns (Removed inspectionId and deviceId) */}
              <th className="border p-2">ID</th>
              <th className="border p-2">Date</th>
              <th className="border p-2">Device Part</th>
              <th className="border p-2">Condition</th>
              <th className="border p-2">Remedy</th>
              <th className="border p-2">Notes</th>
              <th className="border p-2">Resolution</th>
              <th className="border p-2">Status</th>
              <th className="border p-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {violations.length === 0 ? (
              <tr>
                <td colSpan={14} className="border p-2 text-center">
                  No violations found
                </td>
              </tr>
            ) : (
              violations.map((vio) => {
                // Parse numeric or letter codes from the stored labels
                const pCode = parseNumericCode(vio.devicePart || '');
                const vCode = parseLetterCode(vio.violatingCondition || '');
                const rCode = parseNumericCode(vio.suggestedRemedy || '');

                return (
                  <tr key={vio.violationId} className="hover:bg-gray-50">
                    <td className="border p-2 text-center">{pCode}</td>
                    <td className="border p-2 text-center">{vCode}</td>
                    <td className="border p-2 text-center">{rCode}</td>

                    <td className="border p-2">{vio.violationId ?? ''}</td>
                    <td className="border p-2">{vio.violationDate || ''}</td>
                    <td className="border p-2">{vio.devicePart || ''}</td>
                    <td className="border p-2">{vio.violatingCondition || ''}</td>
                    <td className="border p-2">{vio.suggestedRemedy || ''}</td>
                    <td className="border p-2">{vio.additionalComment || ''}</td>
                    <td className="border p-2">{vio.resolutionMethod || ''}</td>
                    <td className="border p-2">{vio.itemStatus || ''}</td>
                    <td className="border p-2 text-right">
                      ${vio.estimatedAmount != null ? vio.estimatedAmount : ''}.00
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViolationsTable;
